import { Tab, Tabs } from "@blueprintjs/core";
import React from "react";
import styled from "styled-components";
import ExampleA from "../assets/landing-example-1.png";
import ExampleB from "../assets/landing-example-2.png";
import ExampleC from "../assets/landing-example-3.png";
import ExampleD from "../assets/landing-example-4.png";
import ExampleE from "../assets/landing-example-5.png";

const SemgrepExamplePanel = styled.div`
  & .bp3-tabs {
    flex-wrap: wrap;
  }
  & .bp3-tab {
    &[aria-selected="true"] {
      :hover {
        color: #ffffff;
        opacity: 0.7;
      }
      color: #ffffff;
      background-color: rgba(97, 54, 245, 0.7) !important;
    }
    :hover {
      color: var(--global-purple);
      opacity: 0.7;
    }
    color: var(--global-purple);
  }
`;
const SemgrepExampleContainer = styled.div`
  max-width: 420px;
`;
const SemgrepImgContainer = styled.div`
  display: inline-block;
  max-width: 400px;
  overflow-x: auto;
  border: 1px solid var(--global-mid-gray);
  border-radius: 3px;
  font-size: 0px;
  line-height: 0px;
`;
const SemgrepExample: React.FC<{
  src: string;
  alt: string;
  caption: any;
}> = ({ src, alt, caption }) => (
  <SemgrepExampleContainer>
    <SemgrepImgContainer>
      <img src={src} alt={alt} width="100%" />
    </SemgrepImgContainer>
    {caption}
  </SemgrepExampleContainer>
);

export const SemgrepExamplesPanel: React.FC<{}> = () => {
  return (
    <SemgrepExamplePanel>
      <Tabs id="examples-section" vertical>
        <Tab
          id="example-1"
          title={<code>print(...)</code>}
          panel={
            <SemgrepExample
              src={ExampleA}
              alt="Detect shell execution"
              caption={
                <div>
                  Have you ever accidentally left a print statement in your code
                  and then committed it? It’s easy to write a Semgrep rule to
                  find a function call like <code>print</code>.
                  <br />
                  <br />
                  <div>
                    Semgrep can even provide an autofix for line 6, like
                    replacing it with:{" "}
                    <code>{`logger.info(f'DEBUG: {skynet.iv}')`}</code>
                  </div>
                </div>
              }
            />
          }
        />
        <Tab
          id="example-2"
          title={<code>$X == $X</code>}
          panel={
            <SemgrepExample
              src={ExampleB}
              alt="Enforce coding best practices"
              caption={
                <div>
                  Line 5 is always true!{" "}
                  <code>
                    obj_<b>a</b>
                  </code>{" "}
                  should be compared with{" "}
                  <code>
                    obj_<b>b</b>
                  </code>
                  .
                  <br />
                  <br />
                  The Semgrep pattern <em>$X == $X</em> will match any code
                  where both sides of the operator (==) are the same.
                </div>
              }
            />
          }
        />
        <Tab
          id="example-3"
          title={
            <span>
              <code>boto3.client(...)</code>
            </span>
          }
          panel={
            <SemgrepExample
              src={ExampleC}
              alt="Ensure use of secure defaults"
              caption={
                <div>
                  Note that Semgrep will still match <code>boto3</code>, even
                  though we renamed it in the import.
                  <br />
                  <br />
                  We could narrow this query to look only for instances where
                  key_id and secret_key are being hardcoded:{" "}
                  <code>boto3.client(..., key_id="...", secret_key="...)</code>
                  <br />
                  <br />
                  This Semgrep query would still match even if the order of the
                  keyword arguments were swapped, since in Python the order of
                  keyword arguments does not matter.
                </div>
              }
            />
          }
        />
        <Tab
          id="example-4"
          title={<code>hello('world')</code>}
          panel={
            <SemgrepExample
              src={ExampleD}
              alt="Ensure use of secure defaults"
              caption={
                <div>
                  Semgrep does constant propagation, automatically understanding
                  that <code>hello(x)</code> is equivalent to{" "}
                  <code>hello('world')</code>.
                </div>
              }
            />
          }
        />
        <Tab
          id="example-5"
          title={<code>foo(1)</code>}
          panel={
            <SemgrepExample
              src={ExampleE}
              alt="Ensure use of secure defaults"
              caption={
                <div>
                  A Semgrep snippet that illustrates advanced intraprocedural
                  dataflow capabilities. In the above example,{" "}
                  <code>foo(x)</code> is matched by the pattern <em>foo(1)</em>{" "}
                  because if <code>c</code> is true,
                  <code>foo(1)</code> could be the semantic meaning of the{" "}
                  <code>foo(x)</code> call. This illustrates flow-sensitivity to
                  conditionals and gotos.
                  <br />
                  <br />
                  Try to do this with grep :)
                </div>
              }
            />
          }
        />
      </Tabs>
    </SemgrepExamplePanel>
  );
};
